"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { MOT_COOKIE_PREFERENCES_CHANGED } from "@/components/CookieConsent";
import { isMarketingAllowed } from "@/lib/analytics-consent";
import { jsonForInlineScript } from "@/lib/ga4-consent";
import { isProdPublicTrackingEnabled } from "@/lib/public-deploy-env";
import { onMarketingAllowanceChanged } from "@/lib/vendor-tracking-lifecycle";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

const META_PIXEL_ID = /^\d{5,32}$/;

export function MetaPixelLoader({ pixelId }: { pixelId: string | undefined }) {
  const pathname = usePathname();
  const [enabled, setEnabled] = useState(false);
  const [generation, setGeneration] = useState(0);
  const previousPath = useRef<string | null>(null);
  const wasEnabled = useRef(false);
  const prod = isProdPublicTrackingEnabled();
  const id = pixelId?.trim() ?? "";
  const validId = META_PIXEL_ID.test(id);

  const applyAllowance = useCallback((allowed: boolean) => {
    if (!allowed) {
      onMarketingAllowanceChanged(false);
      wasEnabled.current = false;
      previousPath.current = null;
      setEnabled(false);
      return;
    }
    if (!wasEnabled.current) setGeneration((value) => value + 1);
    wasEnabled.current = true;
    setEnabled(true);
  }, []);

  useEffect(() => {
    if (!prod || !validId) return;
    applyAllowance(isMarketingAllowed());
    const onPreferencesChanged = () => applyAllowance(isMarketingAllowed());
    window.addEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPreferencesChanged);
    return () => window.removeEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPreferencesChanged);
  }, [applyAllowance, prod, validId]);

  useEffect(() => {
    if (!enabled) return;
    if (previousPath.current === null) {
      previousPath.current = pathname;
      return;
    }
    if (previousPath.current !== pathname && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
      previousPath.current = pathname;
    }
  }, [enabled, pathname]);

  if (!prod || !validId || !enabled) return null;
  const safeId = jsonForInlineScript(id);
  return (
    <Script id={`mot-meta-pixel-${generation}`} strategy="afterInteractive">
      {`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('consent','grant');fbq('init',${safeId});fbq('track','PageView');`}
    </Script>
  );
}
